import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import NewsletterSignup from '../components/NewsletterSignup';
import AnimatedCounter from '../components/AnimatedCounter';
import Card3D from '../components/Card3D';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import ReviewBadges from '../components/ReviewBadges';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Home as HomeIcon, 
  Building2, 
  Factory, 
  Award, 
  Users, 
  Shield, 
  Clock, 
  Star, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  Droplets,
  Hammer,
  Calculator
} from 'lucide-react';
import { BUSINESS } from '../config/business';
import MultiStepQuoteForm from '../components/MultiStepQuoteForm';
import GoogleReviews from '../components/GoogleReviews';

const Home = () => {
  const featuredServices = [
    {
      id: 1,
      title: 'Garage Floor Coating',
      description: 'Transform your garage into a stunning, durable space that adds value to your home.',
      icon: HomeIcon,
      link: '/garage-floor-coating',
      features: ['Slip Resistant', 'Easy to Clean', 'Chemical Resistant']
    },
    {
      id: 2,
      title: 'Metallic Epoxy',
      description: 'Create breathtaking 3D metallic effects that make your floors a work of art.',
      icon: Sparkles,
      link: '/metallic-epoxy',
      features: ['Unique Designs', 'Luxurious Look', 'Seamless Finish']
    },
    {
      id: 3,
      title: 'Flake System',
      description: 'Decorative flake floors perfect for garages, patios, and commercial spaces.',
      icon: Droplets,
      link: '/flake-system',
      features: ['UV Resistant', 'Customizable', 'High Durability']
    },
    {
      id: 4,
      title: 'Pool Deck Resurfacing',
      description: 'Non-slip, beautiful surfaces perfect for Florida pool areas.',
      icon: Building2,
      link: '/pool-deck-resurfacing',
      features: ['Heat Reflective', 'Waterproof', 'Safe Surface']
    },
    {
      id: 5,
      title: 'Commercial Epoxy',
      description: 'Heavy-duty flooring solutions for retail, offices, and commercial spaces.',
      icon: Building2,
      link: '/commercial-epoxy',
      features: ['Fast Installation', 'Low Maintenance', 'Professional Look']
    },
    {
      id: 6,
      title: 'Industrial Epoxy',
      description: 'Maximum durability for warehouses, factories, and industrial facilities.',
      icon: Factory,
      link: '/industrial-epoxy',
      features: ['Heavy Traffic', 'Chemical Proof', 'Long Lasting']
    }
  ];

  const scrollToQuote = () => {
    document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Epoxy Flooring Naples FL | Garage Floor Coatings | EpoxyArt33"
        description="Epoxy flooring & garage floor coatings in Naples FL. Flake, metallic & polyaspartic systems, lifetime residential warranty, 500+ projects. Free estimate: (239) 276-1462."
        keywords="epoxy flooring Naples FL, garage floor coating Naples FL, epoxy floor Naples, polyaspartic coating Naples, metallic epoxy Naples, flake epoxy floors Naples, concrete coatings Naples FL, epoxy art 33"
        url="/"
      />
      <SchemaMarkup />
      <Navbar />

      {/* Trust Bar - Above the fold */}
      <div className="bg-brand-gold text-brand-black py-2 text-center text-sm font-medium mt-[80px]">
        <span className="inline-flex items-center gap-2 flex-wrap justify-center">
          <Shield className="w-4 h-4" /> <Link to="/warranty" className="underline underline-offset-2 hover:text-gray-800" data-testid="home-warranty-link">Lifetime Residential Warranty</Link> • OSHA 30 Certified • Licensed & Insured
        </span>
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div>
              <Badge className="mb-6 bg-brand-gold/20 text-brand-gold hover:bg-brand-gold/30 px-4 py-1.5 text-base border border-brand-gold/30">
                #1 Epoxy Flooring Company in Naples, FL
              </Badge>
            </div>
            
            {/* H1 - Exact match keyword | NO animation = optimal LCP */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Epoxy Flooring & Garage Floor Coatings in Naples, FL
            </h1>
            
            <p 
              className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Naples' local epoxy floor installers — serving Golden Gate Estates, North Naples, East Naples, Pelican Bay, Marco Island, Bonita Springs & Estero. 
              <strong className="text-gray-900"> 1-Day installation. Lifetime warranty. No hot tire pickup.</strong>
            </p>

            {/* Trust Signals — Render immediately (no animation) */}
            <div 
              className="flex flex-wrap justify-center gap-4 mb-6 text-sm text-gray-600"
            >
              <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" /> 500+ Projects Completed</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" /> 100% Solids Epoxy</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" /> UV-Stable Topcoat</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" /> Moisture Barrier Included</span>
            </div>

            {/* Review Badges — Render immediately */}
            <div className="mb-8">
              <ReviewBadges />
            </div>
            
            <div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <div>
                <Button onClick={scrollToQuote} size="lg" className="bg-brand-gold hover:bg-brand-gold-light text-brand-black text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-shadow font-bold hover:scale-105 active:scale-95">
                  Get Free Estimate <ChevronRight className="ml-2" />
                </Button>
              </div>
              <a href="tel:2392761462">
                <div>
                  <Button size="lg" variant="outline" className="border-2 border-brand-gold text-brand-gold hover:bg-brand-gold/10 text-lg px-8 py-6 hover:scale-105 active:scale-95">
                    <Phone className="mr-2 w-5 h-5" /> (239) 276-1462
                  </Button>
                </div>
              </a>
            </div>

            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link to="/cost-calculator" className="inline-flex items-center gap-2 text-brand-gold-dark font-semibold hover:underline underline-offset-4" data-testid="home-hero-calculator-link">
                <Calculator className="w-5 h-5" /> Or calculate your cost in 30 seconds →
              </Link>
              <Link to="/ai-visualizer" className="inline-flex items-center gap-2 text-brand-gold-dark font-semibold hover:underline underline-offset-4" data-testid="home-hero-visualizer-link">
                <Sparkles className="w-5 h-5" /> See your floor with AI — upload a photo →
              </Link>
            </div>
          </div>

          {/* Stats with Animated Counters */}
          <ScrollReveal>
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto" data-testid="home-stats-grid">
              <motion.div 
                className="text-center p-6 rounded-lg bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-gold/20"
                whileHover={{ y: -5, scale: 1.05 }}
                data-testid="stat-projects"
              >
                <div className="text-4xl font-bold text-brand-gold mb-2">
                  <AnimatedCounter end={BUSINESS.stats.projects} suffix="+" />
                </div>
                <div className="text-gray-600 font-medium">Projects Completed</div>
              </motion.div>
              
              <motion.div 
                className="text-center p-6 rounded-lg bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-gold/20"
                whileHover={{ y: -5, scale: 1.05 }}
                data-testid="stat-happy-clients"
              >
                <div className="text-4xl font-bold text-brand-gold mb-2">
                  <AnimatedCounter end={BUSINESS.stats.happyClients} suffix="+" />
                </div>
                <div className="text-gray-600 font-medium">Happy Clients</div>
              </motion.div>
              
              <motion.div 
                className="text-center p-6 rounded-lg bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-gold/20"
                whileHover={{ y: -5, scale: 1.05 }}
                data-testid="stat-years"
              >
                <div className="text-4xl font-bold text-brand-gold mb-2">
                  <AnimatedCounter end={BUSINESS.stats.yearsExperience} suffix="+" />
                </div>
                <div className="text-gray-600 font-medium">Years Experience</div>
              </motion.div>
              
              <motion.div 
                className="text-center p-6 rounded-lg bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-gold/20"
                whileHover={{ y: -5, scale: 1.05 }}
                data-testid="stat-google-rating"
              >
                <div className="text-4xl font-bold text-brand-gold mb-2">
                  <AnimatedCounter end={BUSINESS.stats.googleRating} decimals={1} suffix="★" />
                </div>
                <div className="text-gray-600 font-medium">Google Rating</div>
              </motion.div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Before/After Showcase Section — Visual Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <ScrollReveal>
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                Real Transformations · Naples FL
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                See What We Do — Before & After
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Real projects, real clients. Hover over each image to see the transformation.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: 'Tropical Breeze Flake — 2-Car Garage',
                location: 'Naples FL · Residential',
                before: '/images/gallery/tropical-breeze-flake-garage/tropical-breeze-1-before-bare-concrete.webp',
                after: '/images/gallery/tropical-breeze-flake-garage/tropical-breeze-3-finished-garage-door-open.webp',
                service: 'garage-floor-coating',
              },
              {
                title: 'Pool Deck Resurfacing',
                location: 'Naples FL · Residential',
                before: '/images/projects/pool-deck-before.webp',
                after: '/images/projects/pool-deck-flake-after-1.webp',
                service: 'pool-deck-resurfacing',
              },
              {
                title: 'Commercial Kitchen Epoxy',
                location: 'Naples FL · Commercial',
                before: '/images/projects/restaurant-epoxy-before1.webp',
                after: '/images/projects/restaurant-epoxy-kitchen.webp',
                service: 'commercial-epoxy',
              },
            ].map((item, index) => (
              <ScrollReveal key={index} delay={index * 0.1} direction="up">
                <Link to={`/${item.service}`} className="block group">
                  <div className="relative aspect-[4/5] rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer">
                    {/* Before image (default) */}
                    <img
                      src={item.before}
                      alt={`${item.title} before - Naples FL`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:opacity-0"
                     width="800" height="600" />
                    {/* After image (on hover) */}
                    <img
                      src={item.after}
                      alt={`${item.title} after - Naples FL by EpoxyArt33`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                     width="800" height="600" />

                    {/* Before/After labels */}
                    <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full group-hover:opacity-0 transition-opacity">
                      BEFORE
                    </div>
                    <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      AFTER
                    </div>

                    {/* Caption */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                      <h3 className="text-white text-xl font-bold mb-1">{item.title}</h3>
                      <p className="text-white text-sm">{item.location}</p>
                      <p className="text-white/80 text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        Hover or tap to see the after →
                      </p>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black text-lg px-8 py-6 font-bold">
                View Full Gallery (90+ Projects) <ChevronRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Residential & Commercial Epoxy Flooring Solutions Naples FL</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Specialized epoxy systems for every Naples home and business — from Golden Gate Estates to Marco Island</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {featuredServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <ScrollReveal key={service.id} delay={index * 0.1} direction="up">
                  <Card3D className="h-full">
                    <Card className="border-2 border-gray-200 hover:border-brand-gold hover:shadow-2xl transition-all duration-300 group h-full">
                      <CardHeader>
                        <motion.div 
                          className="w-14 h-14 bg-brand-gold/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-gold transition-colors"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          <Icon className="w-7 h-7 text-brand-gold group-hover:text-white transition-colors" />
                        </motion.div>
                        <CardTitle className="text-2xl text-gray-900">{service.title}</CardTitle>
                        <CardDescription className="text-gray-600 text-base">{service.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 mb-4">
                          {service.features.map((feature, idx) => (
                            <motion.li 
                              key={idx} 
                              className="flex items-center text-gray-700"
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              viewport={{ once: true }}
                            >
                              <CheckCircle2 className="w-5 h-5 text-brand-gold mr-2 flex-shrink-0" />
                              {feature}
                            </motion.li>
                          ))}
                        </ul>
                        <Link to={service.link}>
                          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button variant="outline" className="w-full border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black" aria-label={`View ${service.title} services in Naples FL`}>
                              {service.title} Services
                            </Button>
                          </motion.div>
                        </Link>
                      </CardContent>
                    </Card>
                  </Card3D>
                </ScrollReveal>
              );
            })}
          </div>

          <ScrollReveal delay={0.4}>
            <div className="text-center mt-12">
              <Link to="/services">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" className="bg-brand-gold hover:bg-brand-gold-dark text-brand-black shadow-lg">
                    View All Services <ChevronRight className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Installation Process - SEO H2 */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="container mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">Our Epoxy Installation Process Naples FL</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Professional preparation is 90% of a successful epoxy floor. Here's how we ensure lasting results.</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {[
              { step: 1, title: 'Diamond Grinding', desc: 'CSP 2-3 profile with industrial grinders for maximum adhesion' },
              { step: 2, title: 'Crack & Surface Repair', desc: 'Fill all cracks, spalls, and divots with epoxy filler' },
              { step: 3, title: 'Moisture Vapor Barrier', desc: 'MVB primer blocks up to 25 lbs moisture - critical for Florida' },
              { step: 4, title: '100% Solids Epoxy Base', desc: 'Chemtec industrial-grade epoxy with zero water/solvents' },
              { step: 5, title: 'Decorative Layer', desc: 'Torginol flakes, metallic pigments, or quartz broadcast' },
              { step: 6, title: 'Polyaspartic Topcoat', desc: 'UV-stable, hot tire resistant clear coat for 15+ years' }
            ].map((item, index) => (
              <ScrollReveal key={index} delay={index * 0.1} direction="up">
                <div className="text-center">
                  <div className="w-14 h-14 bg-brand-gold text-brand-black rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <div className="text-center mt-12">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-gold hover:bg-brand-gold-dark text-brand-black">
                  Get Free Estimate <ChevronRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Why Homeowners & Contractors Choose EpoxyArt33 Naples FL</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Experience the difference that quality materials and expert installation make</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Award, title: 'Commercial-Grade Materials', desc: 'We use 100% solids Chemtec epoxy and Torginol flakes - the same products used in commercial applications.' },
              { icon: Users, title: 'Expert Installation', desc: 'Our OSHA 30 certified technicians bring professional expertise to every residential and commercial project.' },
              { icon: Shield, title: 'Lifetime Warranty', desc: 'We stand behind our residential work with lifetime warranty coverage. Our floors are built to last.' },
              { icon: Clock, title: '1-Day Installation', desc: 'Most projects completed in just 1 day. Walk on it within 24 hours, drive on it within 48-72 hours.' }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <ScrollReveal key={index} delay={index * 0.1} direction="up">
                  <motion.div 
                    className="text-center"
                    whileHover={{ y: -10 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <motion.div 
                      className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon className="w-8 h-8 text-brand-gold" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.desc}</p>
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>

          <ScrollReveal delay={0.5}>
            <div className="text-center mt-12">
              <Link to="/why-epoxyart33">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" variant="outline" className="border-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black shadow-lg" aria-label="Why choose EpoxyArt33 for epoxy flooring in Naples">
                    Why Choose EpoxyArt33 <ChevronRight className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div className="container mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <motion.h2 
                className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Garage Floor Coatings Built for Naples FL Conditions
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Our systems are designed to withstand Florida's heat, humidity, and UV exposure. Slide to see the transformation.
              </motion.p>
            </div>
          </ScrollReveal>
          
          <div className="max-w-4xl mx-auto mb-12">
            <BeforeAfterSlider 
              beforeImage="/images/projects/garage-flake-before-new.webp"
              afterImage="/images/projects/garage-flake-after-new.webp"
              altText="Garage Flake Floor Coating Naples FL"
            />
          </div>

          <ScrollReveal delay={0.3}>
            <div className="text-center">
              <Link to="/gallery">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="bg-brand-gold hover:bg-brand-gold-dark text-brand-black font-bold shadow-lg hover:shadow-xl transition-shadow">
                    View Full Gallery <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Testimonials — real Google reviews */}
      <GoogleReviews limit={6} />

      {/* Service Areas */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-gold">
        <div className="container mx-auto">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Serving Every Naples Neighborhood</h2>
            <p className="text-xl text-white mb-6" data-testid="home-naples-areas">Naples • North Naples • East Naples • Golden Gate Estates • Golden Gate City • Pelican Bay • Vineyards • Lely Resort • Ave Maria • Marco Island • Bonita Springs • Estero</p>
            <p className="text-base text-white/90 mb-6 max-w-3xl mx-auto" data-testid="home-near-me-text">
              Searching "epoxy flooring companies near me"? You found Naples' local, licensed <Link to="/epoxy-floor-installers-naples-fl" className="underline underline-offset-4 font-semibold hover:text-white">epoxy floor installers</Link> — epoxy coating garage floors, <Link to="/polyaspartic-floor-coating-naples-fl" className="underline underline-offset-4 font-semibold hover:text-white">polyaspartic flooring</Link>, pool decks and commercial coatings with transparent <Link to="/epoxy-floor-installation-cost-naples-fl" className="underline underline-offset-4 font-semibold hover:text-white">2026 pricing</Link>.
            </p>
            <a href="tel:2392761462">
              <Button size="lg" className="bg-white text-brand-gold hover:bg-gray-100">
                <Phone className="mr-2" />
                Call Us: (239) 276-1462
              </Button>
            </a>
            <p className="mt-5 text-sm text-white/80">
              <Link to="/service-areas" className="underline underline-offset-4 hover:text-white" data-testid="home-other-areas-link">Also serving Fort Myers, Cape Coral & Southwest Florida →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Cost Calculator promo */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-black text-white" data-testid="home-calculator-banner">
        <div className="container mx-auto max-w-4xl text-center">
          <Calculator className="w-14 h-14 text-brand-gold mx-auto mb-5" />
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-gold mb-3">How Much Will Your New Floor Cost?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Pick your project, set the size, and see your real Naples price range instantly — garage, pool deck, metallic or commercial.
          </p>
          <Link to="/cost-calculator">
            <Button size="lg" className="bg-brand-gold hover:bg-brand-gold-light text-brand-black font-bold text-lg px-8 py-6 hover:scale-105 active:scale-95" data-testid="home-calculator-banner-btn">
              Try the Free Cost Calculator <ChevronRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Get Your Free Quote Naples FL</h2>
            <p className="text-xl text-gray-600">Tell us about your project and we'll provide a detailed estimate within 24 hours</p>
          </div>

          <MultiStepQuoteForm />
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <Mail className="w-16 h-16 text-black mx-auto mb-6" />
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4">
              Get Exclusive Offers & Tips
            </h2>
            <p className="text-xl text-gray-900 mb-8">
              Join our newsletter for epoxy flooring tips, special promotions, and inspiration. No spam, unsubscribe anytime.
            </p>
            <div className="flex justify-center">
              <NewsletterSignup />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;